import React, { useState, useEffect } from "react";
import { ApplyNew } from "../../components/application";
import AppClass from "../../AppClass";
import Axios from "axios";
import moment from "moment";
import toastMessage from "../../components/toastMessage";
import {
  getLoanAmortization,
  getTotalInterest,
  sumFees,
} from "../../utils/loan_amortization";
import HOST_URL from "../../configs/api";

const transformFeesArrayToObject = (feesArray) => {
  return feesArray?.reduce((acc, fee) => {
    acc[fee.name] = fee.amount;
    return acc;
  }, {});
};

const transformFeesObjectToArray = (feesObject) => {
  return Object.entries(feesObject)
    .filter(([_, value]) => value !== '')
    .map(([name, amount]) => ({
      name,
      amount: Number(amount)
    }));
};

const getDurationInMonths = (duration, duration_unit) => {
  switch (duration_unit) {
    case 'years':
      return duration * 12;
    case 'months':
      return duration;
    case 'weeks':
      return duration / 4;
    case 'days':
      return duration / 30;
    default:
      return duration;
  }
}

// Hardcoded insurance fee computation for 2KW
const computeInsuranceFee = (principal_amount, duration, duration_unit) => {
  const durationInMonths = getDurationInMonths(duration, duration_unit);
  const fee = Math.ceil(((5.03 * Number(durationInMonths)) + (3.03 * Number(principal_amount))) / 6000);
  return fee;
}

const ApplyLoan = (props) => {
  // Core loan data
  const [loanData, setLoanData] = useState({
    app_id: "",
    loan_id: "",
    member_id: "",
    pre_onboarding_payment: 0,
    principal_amount: 0,
    interest_rate: 0,
    interest_frequency: "annually",
    amortization_method: "fixed_interest",
    duration: 1,
    duration_unit: "months",
    start_date: moment().format("YYYY-MM-DD"),
    loan_product_id: "",
    fees: {},
  });

  const [amortizationSchedule, setAmortizationSchedule] = useState(null);

  // UI and reference data states
  const [sacco, setSacco] = useState("");
  const [apps, setApps] = useState([]);
  const [member, setMember] = useState("");
  const [members, setMembers] = useState([]);
  const [loanProducts, setLoanProducts] = useState([]);
  const [settings, setSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGettingLoanById, setIsGettingLoanById] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [applicableFees, setApplicableFees] = useState({});

  useEffect(() => {
    const initializeComponent = async () => {
      const app_options = props.apps.map(el => ({
        label: el.name,
        value: el._id,
      }));
      setApps(app_options);
    };
    initializeComponent();
  }, []);

  useEffect(() => {
    if (props?.somethingID && apps?.length > 0) {
      getLoanById();
    }
  }, [props?.somethingID, apps?.length]);

  const validateLoanData = () => {
    const errors = {};

    if (!loanData.app_id) {
      errors.app_id = 'Sacco is required';
    }

    if (!loanData.member_id) {
      errors.member_id = 'Member is required';
    }

    if (!loanData.loan_product_id) {
      errors.loan_product_id = 'Loan product is required';
    }

    if (!loanData.amortization_method) {
      errors.amortization_method = 'Amortization method is required';
    }

    if (!loanData?.principal_amount || Number(loanData?.principal_amount) <= 0) {
      errors.principal_amount = 'Principal amount must be greater than 0';
    }

    if (Number(loanData?.interest_rate) < 0) {
      errors.interest_rate = 'Interest rate must be greater than or equal to 0';
    }

    if (!loanData?.duration || Number(loanData?.duration) <= 0) {
      errors.duration = 'Duration must be greater than 0';
    }

    if (!loanData.start_date) {
      errors.start_date = 'Start date is required';
    }

    return errors;
  };

  const getLoanById = async () => {
    setIsGettingLoanById(true);
    try {
      const response = await AppClass.get_loan_by_id(props.somethingID);
      
      if (response?.data?.data) {
        const loanDetails = response?.data?.data;
        const selectedSacco = apps.find(el => el.value === loanDetails.app_id);
        
        if (selectedSacco) {
          getLoanProducts(selectedSacco.value);
          getMembers(selectedSacco.value);
          getLoanId(selectedSacco.value);
          getSettings(selectedSacco.value);

          // Transform fees array to object for the form
          const transformedFees = transformFeesArrayToObject(loanDetails.fees);
          setApplicableFees(transformedFees);

          setLoanData(prev => ({
            ...prev,
            app_id: selectedSacco?.value,
            loan_id: loanDetails?.loan_id,
            member_id: loanDetails?.member_id,
            pre_onboarding_payment: loanDetails?.pre_onboarding_payment,
            principal_amount: loanDetails?.principal_amount,
            interest_rate: loanDetails?.interest_rate,
            interest_frequency: loanDetails?.interest_frequency,
            amortization_method: loanDetails?.amortization_method,
            duration: loanDetails?.duration,
            duration_unit: loanDetails?.duration_unit,
            start_date: moment(loanDetails?.start_date).format("YYYY-MM-DD"),
            loan_product_id: loanDetails?.loan_product_id,
          }));
        }
      }
    } catch (error) {
      toastMessage("error", error?.response?.data?.error || error?.message || "Failed to fetch loan details");
    } finally {
      setIsGettingLoanById(false);
    }
  };

  const onChangeSacco = async (selectedSacco) => {
    setSacco(selectedSacco);
    setMembers([]);
    setMember("");
    setLoanProducts([]);
    setLoanData(prev => ({
      ...prev,
      app_id: selectedSacco.value,
      loan_product_id: "",
      start_date: moment().format("YYYY-MM-DD"),
    }));

    try {
      await Promise.all([
        getLoanProducts(selectedSacco.value),
        getMembers(selectedSacco.value),
        getLoanId(selectedSacco.value),
        getSettings(selectedSacco.value),
      ]);
    } catch (error) {
      toastMessage("error", error?.message);
    }
  };

  const onChangeLoanProduct = (e) => {
    const productId = e?.value;
    const product = loanProducts.find(p => p._id === productId);
    
    if (product) {
      setLoanData(prev => ({
        ...prev,
        loan_product_id: product._id,
        interest_rate: product.interest_rate || 0,
        duration: product.duration || 1,
        duration_unit: product.duration_unit || 'months',
        interest_frequency: product.interest_frequency || 'annually',
        amortization_method: product.amortization_method || 'fixed_interest',
      }));
    }
  };

  const onLoanDataChange = (e) => {
    const { name, value } = e?.target;
    // const isNumberField = e?.target?.__reactProps$s4fsgus554?.type === 'number'

    setLoanData(prev => ({
      ...prev,
      [name]: value
    }));

    // hardcoded insurance fee computation for 2KW
    if (name === 'principal_amount' && loanData?.app_id === 'b906a7c7-3ec7-4229-8079-45e4699d67b7') {
      const insuranceFee = computeInsuranceFee(Number(value), loanData?.duration, loanData?.duration_unit);
      setLoanData(prev => ({
        ...prev,
        fees: {
          ...prev.fees,
          insurance: insuranceFee
        }
      }));
    }
  };

  const onChangeMember = async (e) => {
    let member = e;

    setLoanData(prev => ({
      ...prev,
      member_id: member.value,
    }))

    getFleets({
      isLoading: true,
      appid: sacco.value,
      clientid: member?.user_id,
    });
  };

  const onSubmitLoanData = async () => {
    try {
      setIsSubmitting(true);
      
      const errors = validateLoanData();
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      // Transform fees from object to array
      const processedFees = transformFeesObjectToArray(loanData?.fees);

      const submitData = {
        ...loanData,
        principal_amount: Number(loanData.principal_amount),
        interest_rate: Number(loanData.interest_rate),
        duration: Number(loanData.duration),
        fees: processedFees
      };

      let response;
      if (props.somethingID) {
        response = await AppClass.update_data(props.somethingID, submitData);
      } else {
        response = await AppClass.add_data(submitData);
      }
      toastMessage("success", response?.data?.message || "Loan application submitted successfully");
      
      if (props.role === "admin") {
        window.location.href = `/home/admin/view_loan_application/${response?.data?.data?._id}`;
      }

    } catch (error) {
      toastMessage("error", error?.response?.data?.error || error?.message || "Failed to submit loan application");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onGenerateLoanAmortization = async () => {
    setIsSubmitting(true);
    
    try { 
      const errors = validateLoanData();
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      const response = await AppClass.generate_amortization_schedule({
        amortization_method: loanData.amortization_method,
        principal_amount: loanData.principal_amount,
        interest_rate: loanData.interest_rate,
        interest_frequency: loanData.interest_frequency,
        duration: loanData.duration,
        duration_unit: loanData.duration_unit,
        start_date: loanData.start_date,
      });
      setAmortizationSchedule(response?.data?.data);
    } catch (error) {
      toastMessage("error", error?.response?.data?.error || "Failed to generate loan amortization");
    } finally {
      setIsSubmitting(false);
    }
  }

  const getSettings = async (saccoId) => {
    setIsLoading(true);
    try {
      const response = await AppClass.get_setting_data({ tag: "by_appid", app_ids: [saccoId] });
      if (response?.data?.length > 0) {
        setSettings(response?.data?.[0]);
        
        const feesObj = {};
        Object.keys(response?.data?.[0]?.fees || {}).forEach(fee => {
          if (response?.data?.[0]?.fees[fee] === true) {
            feesObj[fee] = '';
          }
        });
        setApplicableFees(feesObj);
      }
    } catch (error) {
      toastMessage("error", error?.response?.data?.error || error?.message);
    } finally {
      setIsLoading(false);
    }
  }

  const getLoanProducts = async (appId) => {
    try {
      const response = await AppClass.get_loan_products({ app_id: appId });
      setLoanProducts(response.data.data);
    } catch (error) {
      toastMessage("error", error?.response?.data?.message || error?.message);
    }
  };

  const getMembers = async (appId) => {
    try {
      const membersList = await AppClass.getRegisteredMembers(
        appId,
        !props.admin ? props.user_id : undefined
      );

      setMembers(membersList);
    } catch (error) {
      toastMessage("error", error?.message);
    }
  };

  const getLoanId = async (appId) => {
    try {
      const data = await AppClass.getLoanId(appId);
      setLoanData(prev => ({
        ...prev,
        loan_id: data.data.data.loan_id,
      }));
    } catch (error) {
      toastMessage("error", error?.response?.data?.error || error?.message);
    }
  };

  const getFleets = async ({ appid, clientid }) => {
    const config = {
      method: "GET",
      url: HOST_URL.mobitill_url + "/fleets",
      params: {
        ["appid"]: [appid],
        ["clientid"]: [clientid],
      },
      headers: {
        Authorization: "Bearer " + props.ztoken,
      },
    };

    try {
      const data = await Axios(config);
        const fleets = data.data.data;
        const fleets_array = [];

        for (let i = 0; i < fleets.length; i++) { 
          fleets_array.push({
            value: fleets[i].vehicleno,
            label: fleets[i].vehicleno,
          });
        }

    } catch (error) {
      toastMessage("error", error?.response?.data?.error || error?.response?.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add a fees change handler
  const onChangeFees = (feeName, e) => {
    const value = e.target.value;
    setLoanData(prev => ({
      ...prev,
      fees: {
        ...prev.fees,
        [feeName]: value === '' ? '' : Number(value)
      }
    }));
  };

    return (
      <div
        className="content-cont"
        style={{ height: "100%", overflowY: "auto" }}
      >
        <ApplyNew
          {...props}
          loanData={loanData}
          sacco={sacco}
          apps={apps}
          member={member}
          members={members}
          loanProducts={loanProducts}
          isSubmitting={isSubmitting}
          validationErrors={validationErrors}
          applicableFees={applicableFees}
          amortizationSchedule={amortizationSchedule}
          onChangeFees={onChangeFees}
          onLoanDataChange={onLoanDataChange}
          onGenerateLoanAmortization={onGenerateLoanAmortization}
          onChangeSacco={onChangeSacco}
          onChangeMember={onChangeMember}
          onChangeLoanProduct={onChangeLoanProduct}
          onSubmitLoanData={onSubmitLoanData}
          />
      </div>
    );
};

export default ApplyLoan;
